import { useMemo } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router-dom'

import { Avatar, ChannelAvatar, Heading, Text } from '@buffer-mono/popcorn'
import {
  getChannelAdditionalInfo,
  getChannelName,
  isChannelType,
} from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { mapPostStatusToTab } from '~publish/helpers/post'
import { isOfType } from '~publish/helpers/typeGuards'
import { channel as channelPage } from '~publish/legacy/routes'

import {
  usePostData,
  useMetadataThreadsTopic,
} from '../PostCard/PostCardContext'

import styles from './ChannelInfo.module.css'

export const ChannelInfo = (): JSX.Element | null => {
  const { status, channel, metadata } = usePostData()
  const threadsTopic = useMetadataThreadsTopic(metadata)

  const channelLink = channelPage.getRoute(channel.id, {
    tab: mapPostStatusToTab(status),
  })

  const channelDisplayName = getChannelNameForDisplay(channel)
  const serviceName = getChannelName(channel.service)
  const channelLinkLabel = channelDisplayName
    ? `Open ${channelDisplayName} ${serviceName} channel`
    : `Open ${serviceName} channel`

  const channelInfo = useMemo(
    () => getChannelAdditionalInfo(channel),
    [channel],
  )

  const pinterestBoard =
    isOfType(metadata, 'PinterestPostMetadata') && metadata.board

  const hasSecondaryInfo = channelInfo || pinterestBoard

  return (
    <div className={styles.container}>
      <Link
        to={channelLink}
        className={styles.avatar}
        aria-label={channelLinkLabel}
      >
        {isChannelType(channel.service) ? (
          <ChannelAvatar
            size="small"
            alt=""
            src={channel.avatar ?? undefined}
            channel={channel.service}
          />
        ) : (
          <Avatar size="small" alt="" src={channel.avatar ?? undefined} />
        )}
      </Link>

      <div className={styles.info}>
        <Heading as="p" size="xs" truncate translate="no">
          {channelDisplayName}
          {threadsTopic && channel.service === 'threads' && (
            <>
              <span className={styles.topicSeparator}>&gt;</span>
              <span className={styles.topic}>{threadsTopic}</span>
            </>
          )}
        </Heading>
        {hasSecondaryInfo && (
          <Text color="subtle" size="sm" truncate translate="no">
            {channelInfo}
            {channelInfo && pinterestBoard && <span aria-hidden>•</span>}
            {pinterestBoard && (
              <span className={styles.pinterestBoard}>
                <Avatar
                  size="xsmall"
                  src={metadata.board?.avatar ?? undefined}
                  alt={metadata.board?.name || ''}
                />
                {metadata.board?.name}
              </span>
            )}
          </Text>
        )}
      </div>
    </div>
  )
}
